import { DialogComponent } from '@theme/dialog';
import { fetchConfig, parseIntOrDefault } from '@theme/utilities';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const COUNTDOWN_SECONDS = 5;
const MIN_UPDATE_LOADING_MS = 1000;

/**
 * @typedef {object} KawAddToCartModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLAnchorElement} productLink
 * @property {HTMLElement} addedText
 * @property {HTMLImageElement} productImage
 * @property {HTMLInputElement} qtyInput
 * @property {HTMLButtonElement} qtyUpdate
 * @property {HTMLElement} itemCountText
 * @property {HTMLElement} subtotal
 * @property {HTMLButtonElement} continueButton
 */

/**
 * The "added to cart" confirmation modal. Opens whenever any add-to-cart action
 * (product page or collection grid quick-add) resolves, shows the product just added
 * plus running cart totals, and auto-dismisses after a countdown that pauses while the
 * shopper adjusts the quantity stepper.
 *
 * @extends {DialogComponent<KawAddToCartModalRefs>}
 */
class KawAddToCartModal extends DialogComponent {
  requiredRefs = [
    'dialog',
    'productLink',
    'addedText',
    'productImage',
    'qtyInput',
    'qtyUpdate',
    'itemCountText',
    'subtotal',
    'continueButton',
  ];

  #abortController = new AbortController();
  #updateAbortController;
  #countdownInterval;
  #countdownValue = COUNTDOWN_SECONDS;

  /** @type {{ key: string, quantity: number } | null} */
  #currentLine = null;

  connectedCallback() {
    super.connectedCallback();

    // A page restored from the back/forward cache (or a soft page-transition that leaves
    // this element untouched) can carry over a `[open]` dialog from a previous view. This
    // modal is only ever meant to appear right after an add-to-cart, so force it closed on
    // every connect rather than letting stale, unpopulated state show through.
    this.refs.dialog.close();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    // A true back/forward-cache restore resumes this exact document without re-running
    // connectedCallback, so the guard above wouldn't fire again - catch that case here.
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.refs.dialog.close();
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#stopCountdown();
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.action !== 'add') return;

    const merchandiseId = event.lines?.[0]?.merchandiseId;

    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError || !cart) return;

        const items = /** @type {any[]} */ (detail?.items ?? []);
        const item =
          items.find((line) => String(line.variant_id) === String(merchandiseId)) ?? items[items.length - 1];

        if (!item) return;

        this.#renderItem(item);
        this.#renderTotals(cart.totalQuantity, cart.cost.totalAmount.amount);
        this.#openAfterFlyToCart();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[kaw-add-to-cart-modal] Event promise rejected:', error);
      });
  };

  /**
   * Opens the modal once any in-flight `fly-to-cart` animation has landed, so the
   * confirmation never appears ahead of the product image reaching the cart icon.
   */
  #openAfterFlyToCart() {
    const open = () => {
      this.showDialog();
      this.#startCountdown();
    };

    if (!document.querySelector('fly-to-cart')) {
      open();
      return;
    }

    document.addEventListener('fly-to-cart:end', open, { once: true });
  }

  /**
   * @param {any} item - A line item from the Shopify AJAX cart response.
   */
  #renderItem(item) {
    const { productLink, addedText, productImage, qtyInput, qtyUpdate } = this.refs;

    this.#updateAbortController?.abort();
    this.#currentLine = { key: item.key, quantity: item.quantity };

    productLink.textContent = item.product_title;
    productLink.href = item.url;
    addedText.textContent = Theme.translations.kaw_added_to_cart;

    productImage.src = item.image;
    productImage.alt = item.product_title;

    qtyInput.value = String(item.quantity);
    qtyUpdate.hidden = true;
  }

  /**
   * @param {number} totalQuantity
   * @param {string} totalAmount - Decimal string, e.g. "227.50".
   */
  #renderTotals(totalQuantity, totalAmount) {
    const { itemCountText, subtotal } = this.refs;

    const template =
      totalQuantity === 1 ? Theme.translations.kaw_items_in_cart_one : Theme.translations.kaw_items_in_cart_other;
    const countText = template.replace('{{ count }}', String(totalQuantity));
    itemCountText.textContent = countText;
    itemCountText.setAttribute('value', countText);
    itemCountText.removeAttribute('shimmer');

    const cents = convertMoneyToMinorUnits(totalAmount, this.#currency) ?? 0;
    const subtotalText = formatMoney(cents, this.#moneyFormat, this.#currency);
    subtotal.textContent = subtotalText;
    subtotal.setAttribute('value', subtotalText);
    subtotal.removeAttribute('shimmer');
  }

  #startCountdown() {
    this.#stopCountdown();
    this.#countdownValue = COUNTDOWN_SECONDS;
    this.#updateContinueLabel();

    this.#countdownInterval = window.setInterval(() => {
      this.#countdownValue -= 1;

      if (this.#countdownValue <= 0) {
        this.#stopCountdown();
        this.closeDialog();
        return;
      }

      this.#updateContinueLabel();
    }, 1000);
  }

  #stopCountdown() {
    if (!this.#countdownInterval) return;

    window.clearInterval(this.#countdownInterval);
    this.#countdownInterval = undefined;
  }

  #updateContinueLabel() {
    const { continueButton } = this.refs;

    continueButton.textContent = this.#countdownInterval
      ? Theme.translations.kaw_continue_countdown.replace('{{ count }}', String(this.#countdownValue))
      : Theme.translations.kaw_continue;
  }

  decreaseQty() {
    const { qtyInput } = this.refs;
    this.#stageQty(Math.max(1, parseIntOrDefault(qtyInput.value, 1) - 1));
  }

  increaseQty() {
    const { qtyInput } = this.refs;
    this.#stageQty(parseIntOrDefault(qtyInput.value, 1) + 1);
  }

  onQtyInputChange() {
    const { qtyInput } = this.refs;
    this.#stageQty(Math.max(1, parseIntOrDefault(qtyInput.value, 1)));
  }

  /**
   * Adjusts the quantity input locally and reveals the update button, without committing
   * the change to the cart yet - the shopper confirms via {@link updateQty}. Interacting
   * with the stepper stops the auto-dismiss countdown, since the shopper is actively
   * engaging with the modal and it should not vanish under them.
   * @param {number} quantity
   */
  #stageQty(quantity) {
    const { qtyInput, qtyUpdate } = this.refs;

    qtyInput.value = String(quantity);
    qtyUpdate.hidden = quantity === this.#currentLine?.quantity;

    this.#stopCountdown();
    this.#updateContinueLabel();
  }

  updateQty() {
    const { qtyInput } = this.refs;
    this.#commitQty(Math.max(1, parseIntOrDefault(qtyInput.value, 1)));
  }

  /**
   * @param {number} quantity
   */
  #commitQty(quantity) {
    if (!this.#currentLine) return;

    const { qtyUpdate, itemCountText, subtotal } = this.refs;
    const { key } = this.#currentLine;
    qtyUpdate.hidden = true;
    itemCountText.setAttribute('shimmer', '');
    subtotal.setAttribute('shimmer', '');

    this.#updateAbortController?.abort();
    this.#updateAbortController = new AbortController();
    const { signal } = this.#updateAbortController;

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'dialog',
        lines: [{ id: key, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    // A real update usually resolves faster than the shimmer's own animation cycle, so it
    // never gets the chance to actually read as "loading" - hold it up to a floor duration.
    const minLoadingTime = new Promise((resolve) => setTimeout(resolve, MIN_UPDATE_LOADING_MS));

    Promise.all([
      fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body: JSON.stringify({ id: key, quantity }) }),
        signal,
      }).then((response) => response.json()),
      minLoadingTime,
    ])
      .then(([cart]) => {
        if (cart.errors) {
          deferredUpdatePromise.reject(new Error(cart.errors));
          return;
        }

        this.#currentLine = { key, quantity };
        this.#renderTotals(cart.item_count, (cart.total_price / 100).toFixed(2));

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            itemCount: cart.item_count,
            source: 'kaw-add-to-cart-modal',
            didError: false,
          },
        });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error(error);
        deferredUpdatePromise.reject(error);
      });
  }
}

if (!customElements.get('kaw-add-to-cart-modal')) {
  customElements.define('kaw-add-to-cart-modal', KawAddToCartModal);
}
